import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';

// Re-use CommandItem interface (consider moving to a shared location)
interface CommandItem {
  title: string;
  icon: string;
  command: ({ editor, range }: { editor: any; range: any }) => void;
}

@Component({
  selector: 'app-command-suggestion-list',
  templateUrl: './command-suggestion-list.component.html',
  styleUrls: ['./command-suggestion-list.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule],
  changeDetection: ChangeDetectionStrategy.OnPush, // Optimize performance
})
export class CommandSuggestionListComponent implements OnChanges {
  @Input() items: CommandItem[] = [];
  @Input() selectedIndex: number = 0;

  @Output() itemSelected = new EventEmitter<CommandItem>();

  // Inject ElementRef and ChangeDetectorRef
  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  // Method to get the component's root DOM node
  get element(): HTMLElement {
    return this.el.nativeElement;
  }

  // Method to trigger selection
  selectItem(index: number): void {
    const item = this.items[index];
    if (item) {
      this.itemSelected.emit(item);
    }
  }

  // Method to update selected index (called from outside)
  updateSelectedIndex(index: number): void {
    this.selectedIndex = index;
    this.cdr.detectChanges(); // Trigger change detection as index is updated externally
    this.scrollIntoView(); // Scroll the new item into view
  }

  // Method to trigger the currently selected item
  triggerSelectedItem(): void {
    this.selectItem(this.selectedIndex);
  }

  // Scroll the selected item into view
  private scrollIntoView(): void {
    // Use querySelector with caution, might break if template changes
    const selectedElement = this.element.querySelector('.suggestion-item.is-selected');
    selectedElement?.scrollIntoView?.({ block: 'nearest' });
  }

  // Trigger initial scroll when items change
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      // Wait a tick for the DOM to update after items change
      setTimeout(() => this.scrollIntoView(), 0);
    }
  }
}
